import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  FilePlus,
  UploadCloud,
  RotateCw,
  LayoutGrid,
  List,
  ChevronDown,
  BarChart3,
  Users,
} from 'lucide-react';
import { CandiBankEmptySearchIcons } from '../../icons/icons';
import { MainLayout } from '../../ui';
import { AddToInviteListModal } from './Components/AddToInviteListModal';
import { AnalyticsDialog } from './Components/AnalyticsDialog';
import { CandidateCard } from './Components/CandidateCard';
import { CandidateProfileModal } from './Components/CandidateProfileModal';
import { CandidateTable } from './Components/CandidateTable';
import { FilterDrawer } from './Components/FilterDrawer';
import {
  CandidateTableSkeleton,
  SkeletonGrid,
} from './Components/SkeletonLoaders';
import { UseJdModal } from './Components/UseJdModal';
import { ViewInviteListModal } from './Components/ViewInviteListModal';
import { CandiBankUploadModal } from './Components/CandiBankUploadModal';
import { CandiBankUploadWidget } from './Components/CandiBankUploadWidget';
import { BankStatsBar } from './Components/BankStatsBar';
import { useCandiBankStats } from './hooks/useCandiBankStats';
import { toast } from '../../ui/toast';
import { formatResetDate } from '../../utils/types/credits_types';
import { mockCandidates } from './constants/mockData';
import { useCandidateExplorer } from './hooks/UseCandidateExplorer';
import { useCandiBankUpload } from './hooks/useCandiBankUpload';
import { useBankSearch } from './hooks/useBankSearch';
import { BankSearchTab } from './Components/BankSearch/BankSearchTab';

export const CandibankExplorerContent = () => {
  const {
    candidatesPerPage,
    setCandidatesPerPage,
    totalCandidates,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    setViewMode,
    viewMode,
    handleRefresh,
    showAnalyticsDialog,
    setShowAnalyticsDialog,
    setExtractedBehavioralScore,
    extractedBehavioralScore,
    setExtractedKeywords,
    extractedKeywords,
    setExtractedJobTitles,
    extractedJobTitles,
    setExtractedSkill,
    extractedSkill,
    setLoading,
    loading,
    candidates,
    setSearchObject,
    searchObject,
    getMatchColor,
    getStatusText,
    getStatusColor,
    handleCloseProfile,
    showProfileModal,
    selectedCandidate,
    availableLocations,
    handleClearFilters,
    showFilters,
    setShowFilters,
    availableSkills,
    handleFiltersChange,
    filters,
    setShowViewInviteListModal,
    showViewInviteListModal,
    selectedCandidates,
    selectedTransformedCandidates,
    candidateForInvite,
    showInviteModal,
    setShowInviteModal,
    totalPages,
    currentPage,
    handleOpenInviteModal,
    handleViewProfile,
    handleSelectAll,
    handleCandidateSelect,
    isLoading,
    getActiveFiltersCount,
    highScoreCandidates,
    handleBulkInvite,
    setSelectedCandidates,
    isRefreshing,
    showBulkActions,
    showUseJdModal,
    setShowUseJdModal,
    handleAutoFillSearch,
    handleAutoFillAnalytics,
    handleClearSearch,
    handleClearAnalytics,
  } = useCandidateExplorer();

  const [perPageAnchor, setPerPageAnchor] = useState<null | HTMLElement>(null);

  const bankStats = useCandiBankStats();
  const uploadState = useCandiBankUpload(
    () => {
      handleRefresh();
      bankStats.refresh();
    },
    {
      onCredits: bankStats.applyCredits,
      onBlocked: (credits) =>
        toast.error(
          credits
            ? `Monthly CV credit limit reached. 0 of ${credits.monthlyLimit} left, resets ${formatResetDate(credits)}.`
            : 'Monthly CV credit limit reached.',
          { position: 'bottom-center' },
        ),
    },
  );
  const bankSearch = useBankSearch();
  const [activeTab, setActiveTab] = useState<'pool' | 'search'>('pool');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fcfcfd' }}>
      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2, sm: 3 }, maxWidth: 1600, mx: 'auto' }}>
        {/* Header Section */}
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'flex-end' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: -0.5 }}>
              Candidate Explorer
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Manage and discover talent with AI-powered matching
            </Typography>
          </Box>

          <Box>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={(e) => setPerPageAnchor(e.currentTarget)}
              endIcon={<ChevronDown size={14} />}
              sx={{
                bgcolor: 'background.paper',
                borderColor: 'grey.300',
                fontSize: '0.6875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {totalCandidates} Candidates
            </Button>
            <Menu
              anchorEl={perPageAnchor}
              open={Boolean(perPageAnchor)}
              onClose={() => setPerPageAnchor(null)}
            >
              {[25, 50, 100].map((n) => (
                <MenuItem
                  key={n}
                  selected={candidatesPerPage === n}
                  onClick={() => {
                    setCandidatesPerPage(n);
                    setCurrentPage(1);
                    setPerPageAnchor(null);
                  }}
                  sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                >
                  {n} per page
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Stack>

        {/* Bank totals + this month's CV credits */}
        <BankStatsBar stats={bankStats.stats} loading={bankStats.loading} />

        {/* Tab Switcher */}
        <Paper
          elevation={0}
          sx={{
            display: 'inline-flex',
            bgcolor: 'grey.100',
            p: 0.5,
            borderRadius: 2,
            mb: 3,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{ minHeight: 36, '& .MuiTabs-indicator': { display: 'none' } }}
          >
            <Tab
              value="pool"
              label="Talent Pool"
              sx={{
                minHeight: 36,
                py: 0.5,
                px: 2,
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 1.5,
                color: activeTab === 'pool' ? 'text.primary' : 'text.secondary',
                bgcolor: activeTab === 'pool' ? 'background.paper' : 'transparent',
                boxShadow: activeTab === 'pool' ? 1 : 'none',
              }}
            />
            <Tab
              value="search"
              label="Advanced Search"
              sx={{
                minHeight: 36,
                py: 0.5,
                px: 2,
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 1.5,
                color: activeTab === 'search' ? 'text.primary' : 'text.secondary',
                bgcolor: activeTab === 'search' ? 'background.paper' : 'transparent',
                boxShadow: activeTab === 'search' ? 1 : 'none',
              }}
            />
          </Tabs>
        </Paper>

        {activeTab === 'search' ? (
          <BankSearchTab state={bankSearch} />
        ) : (
          <>
            {/* Search and Filter Bar */}
            <Box sx={{ mb: 3 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', maxWidth: 720 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search candidates by name, skills, or location..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={16} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      bgcolor: 'background.paper',
                      '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 1 },
                    }}
                  />
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<FilePlus size={15} />}
                    onClick={() => setShowUseJdModal(true)}
                    sx={{
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderColor: 'grey.300',
                      bgcolor: 'background.paper',
                      py: 1,
                    }}
                  >
                    Use JD
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<UploadCloud size={15} />}
                    onClick={() => uploadState.setShowUploadModal(true)}
                    disabled={bankStats.exhausted}
                    title={
                      bankStats.exhausted && bankStats.credits
                        ? `Monthly CV credit limit reached. Resets ${formatResetDate(bankStats.credits)}.`
                        : undefined
                    }
                    sx={{
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      py: 1,
                      px: 2,
                    }}
                  >
                    Upload CVs
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: { xs: '100%', lg: 'auto' } }}>
                  {/* Grid/List View Switcher */}
                  <Paper
                    elevation={0}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'grey.100',
                      p: 0.5,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setViewMode('grid')}
                      sx={{
                        p: 0.75,
                        borderRadius: 1,
                        bgcolor: viewMode === 'grid' ? 'background.paper' : 'transparent',
                        boxShadow: viewMode === 'grid' ? 1 : 'none',
                      }}
                    >
                      <LayoutGrid size={15} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setViewMode('list')}
                      sx={{
                        p: 0.75,
                        borderRadius: 1,
                        bgcolor: viewMode === 'list' ? 'background.paper' : 'transparent',
                        boxShadow: viewMode === 'list' ? 1 : 'none',
                      }}
                    >
                      <List size={15} />
                    </IconButton>
                  </Paper>

                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    title="Refresh candidates"
                  >
                    <RotateCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                  </IconButton>

                  {showBulkActions && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => setSelectedCandidates([])}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
                    >
                      Clear Selection
                    </Button>
                  )}

                  <Button
                    size="small"
                    variant={selectedCandidates.length > 0 ? 'contained' : 'outlined'}
                    disabled={selectedCandidates.length === 0}
                    startIcon={<FilePlus size={14} />}
                    onClick={handleBulkInvite}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
                  >
                    Invite List {selectedCandidates.length > 0 ? `(${selectedCandidates.length})` : ''}
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<Users size={14} />}
                    onClick={() => setShowViewInviteListModal(true)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
                  >
                    View Invite List
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    color="inherit"
                    startIcon={<BarChart3 size={14} />}
                    onClick={() => setShowAnalyticsDialog(true)}
                    sx={{ bgcolor: 'grey.900', color: 'common.white', textTransform: 'none', fontSize: '0.75rem', fontWeight: 700, '&:hover': { bgcolor: 'grey.800' } }}
                  >
                    Analytics
                  </Button>
                </Stack>
              </Stack>

              {/* Status Stats */}
              <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    <strong>{selectedTransformedCandidates.length}</strong> results found
                  </Typography>
                </Stack>
                {highScoreCandidates > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      <strong style={{ color: '#059669' }}>{highScoreCandidates}</strong> high match
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            {/* Results Header */}
            {!isLoading && selectedTransformedCandidates.length > 0 && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Talent Pool
                </Typography>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                >
                  {selectedCandidates.length === selectedTransformedCandidates.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Stack>
            )}

            {/* Candidates Content Area */}
            <Box sx={{ position: 'relative', minHeight: 400 }}>
              {isLoading ? (
                viewMode === 'grid' ? (
                  <SkeletonGrid count={candidatesPerPage} />
                ) : (
                  <CandidateTableSkeleton />
                )
              ) : selectedTransformedCandidates.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 10, textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    <CandiBankEmptySearchIcons />
                  </Box>
                  {!searchQuery || getActiveFiltersCount() > 0 ? (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
                        No results for your search
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 380, lineHeight: 1.6 }}>
                        We couldn&apos;t find any candidates matching {searchQuery && <strong>&ldquo;{searchQuery}&rdquo;</strong>}
                        {searchQuery && getActiveFiltersCount() > 0 && ' with '}
                        {getActiveFiltersCount() > 0 && <strong>{getActiveFiltersCount()} active filter(s)</strong>}. Try with a new search term or filters.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
                        Your talent pool is empty
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 380, lineHeight: 1.6 }}>
                        No candidates have been added to the database yet. Upload CVs or invite candidates to get started.
                      </Typography>
                    </>
                  )}
                </Stack>
              ) : (
                <MainLayout isLoading={isLoading}>
                  {viewMode === 'grid' ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, 1fr)',
                          lg: 'repeat(3, 1fr)',
                          xl: 'repeat(4, 1fr)',
                          '2xl': 'repeat(5, 1fr)',
                        },
                        gap: 2,
                      }}
                    >
                      {selectedTransformedCandidates.map((candidate) => (
                        <CandidateCard
                          key={candidate.id}
                          candidate={candidate}
                          getMatchColor={getMatchColor}
                          getStatusColor={getStatusColor}
                          getStatusText={getStatusText}
                          isSelected={selectedCandidates.includes(candidate.id)}
                          onSelect={() => handleCandidateSelect(candidate.id)}
                          onViewProfile={handleViewProfile}
                          onInvite={handleOpenInviteModal}
                        />
                      ))}
                    </Box>
                  ) : (
                    <CandidateTable
                      candidates={selectedTransformedCandidates}
                      getMatchColor={getMatchColor}
                      getStatusColor={getStatusColor}
                      getStatusText={getStatusText}
                      selectedCandidates={selectedCandidates}
                      onCandidateSelect={handleCandidateSelect}
                      onSelectAll={handleSelectAll}
                      onViewProfile={handleViewProfile}
                      onInvite={handleOpenInviteModal}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCandidates={totalCandidates}
                      candidatesPerPage={candidatesPerPage}
                      onPageChange={setCurrentPage}
                    />
                  )}
                </MainLayout>
              )}

              <AddToInviteListModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                candidateIds={
                  candidateForInvite ? [candidateForInvite.id] : selectedCandidates
                }
                candidateName={candidateForInvite?.name}
                candidateNames={
                  candidateForInvite
                    ? undefined
                    : selectedTransformedCandidates
                      .filter((c) => selectedCandidates.includes(c.id))
                      .map((c) => c.name)
                }
              />
              <ViewInviteListModal
                isOpen={showViewInviteListModal}
                onClose={() => setShowViewInviteListModal(false)}
              />
            </Box>

            {/* Filter Drawer */}
            <FilterDrawer
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
              availableSkills={availableSkills}
              availableLocations={availableLocations}
            />

            {/* Candidate Profile Modal */}
            <CandidateProfileModal
              candidate={selectedCandidate}
              isOpen={showProfileModal}
              onClose={handleCloseProfile}
              getStatusColor={getStatusColor}
              getStatusText={getStatusText}
              getMatchColor={getMatchColor}
              onInvite={handleOpenInviteModal}
            />

            {/* Analytics Dialog */}
            {showAnalyticsDialog && (
              <AnalyticsDialog
                isOpen={showAnalyticsDialog}
                onClose={() => setShowAnalyticsDialog(false)}
                totalCandidates={mockCandidates.length}
                searchObject={searchObject}
                setSearchObject={setSearchObject}
                candidates={candidates}
                loading={loading}
                setLoading={setLoading}
                extractedSkill={extractedSkill}
                setExtractedSkill={setExtractedSkill}
                extractedJobTitles={extractedJobTitles}
                setExtractedJobTitles={setExtractedJobTitles}
                extractedKeywords={extractedKeywords}
                setExtractedKeywords={setExtractedKeywords}
                extractedBehavioralScore={extractedBehavioralScore}
                setExtractedBehavioralScore={setExtractedBehavioralScore}
              />
            )}

            {/* Use JD Modal */}
            <UseJdModal
              isOpen={showUseJdModal}
              onClose={() => setShowUseJdModal(false)}
              onCreateFromFile={async (file) => {
                await bankSearch.createFromFile(file);
                setActiveTab('search');
              }}
              onCreateFromText={async (text) => {
                await bankSearch.createFromText(text);
                setActiveTab('search');
              }}
              onCreateFromOpening={async (openingId) => {
                await bankSearch.createFromOpening(openingId);
                setActiveTab('search');
              }}
              isCreating={bankSearch.createLoading}
              onAutoFillSearch={handleAutoFillSearch}
              onAutoFillAnalytics={handleAutoFillAnalytics}
              onClearSearch={handleClearSearch}
              onClearAnalytics={handleClearAnalytics}
            />

            {/* Candi Bank Bulk CV Upload Modal & Floating Widget */}
            <CandiBankUploadModal
              isOpen={uploadState.showUploadModal}
              onClose={() => uploadState.setShowUploadModal(false)}
              onFilesSelected={uploadState.addFilesToQueue}
              credits={bankStats.credits}
            />

            <CandiBankUploadWidget
              items={uploadState.items}
              isOpen={uploadState.showWidget}
              isMinimized={uploadState.isWidgetMinimized}
              onMinimizeToggle={() => uploadState.setIsWidgetMinimized((prev) => !prev)}
              onClose={() => uploadState.setShowWidget(false)}
              onRetry={uploadState.retryItem}
              onRemove={uploadState.removeItem}
              onClearCompleted={uploadState.clearCompleted}
              formatFileSize={uploadState.formatFileSize}
              overallProgress={uploadState.overallProgress}
              completedCount={uploadState.completedCount}
              duplicateCount={uploadState.duplicateCount}
              totalCount={uploadState.totalCount}
              isUploading={uploadState.isUploading}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
